import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Brain } from '../icons';
import { supabase } from '../../lib/supabase';
import { MarkdownRenderer } from '../ai/MarkdownRenderer';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AICoachChatProps {
  isOpen: boolean;
  onClose: () => void;
  pathwayContext?: string;
  testMode?: boolean;
}

export function AICoachChat({ isOpen, onClose, pathwayContext, testMode }: AICoachChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error('Your session has expired. Please log in again.');
        }
      }

      // Build conversation history for the Responses API
      const conversationHistory = messages.map(m => ({ role: m.role, content: m.content }));

      const { data, error: fnError } = await supabase.functions.invoke(testMode ? 'ai-coach-test-chat' : 'ai-coach-chat', {
        body: {
          message: userMessage.content,
          conversation_history: conversationHistory,
          ...(pathwayContext ? { pathwayContext } : {}),
        },
      });

      if (fnError) {
        // Try to extract a real error message from the response body
        let errorMsg = fnError.message || 'Failed to get response';
        try {
          const parsed = JSON.parse(fnError.message);
          if (parsed?.error) errorMsg = parsed.error;
        } catch { /* not JSON */ }
        throw new Error(errorMsg);
      }
      if (!data) throw new Error('No response received');

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-4 right-4 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl border border-cream-200 flex flex-col z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-sage-600 to-sage-700 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">AI Coach</h3>
                <p className="text-xs text-sage-100">Text Chat</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-sage-500" />
                </div>
                <p className="text-sm text-chocolate-600 font-medium mb-1">{testMode ? 'Test your AI Coach' : 'Start a conversation'}</p>
                <p className="text-xs text-chocolate-400 max-w-[280px] mx-auto">
                  {testMode
                    ? 'This is a test conversation. Your clients will not see this. Try sending messages to see how your AI Coach responds.'
                    : 'Ask questions about your assessment results, personal development, or coaching goals.'}
                </p>
              </div>
            )}

            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-sage-600 text-white rounded-br-md'
                      : 'bg-cream-100 text-chocolate-800 rounded-bl-md'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                      <MarkdownRenderer content={msg.content} />
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  )}
                  <p className={`text-[10px] mt-1.5 ${
                    msg.role === 'user' ? 'text-sage-200' : 'text-chocolate-400'
                  }`}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </motion.div>
            ))}

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-cream-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}

            {error && (
              <div className="text-center">
                <div className="text-xs text-warm-600 bg-warm-50 px-3 py-2 rounded-lg inline-block">
                  <p>{error}</p>
                  {error.includes('session') && (
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-1.5 text-sage-600 hover:text-sage-700 underline font-medium"
                    >
                      Refresh page
                    </button>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 py-3 border-t border-cream-200 bg-cream-50">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                rows={1}
                className="flex-1 resize-none border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400 bg-white"
                style={{ maxHeight: '100px' }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="p-2.5 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors disabled:opacity-40 disabled:hover:bg-sage-600 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
